<template>
    <page-container>
        <page-header>
            Dashboard
            <template v-slot:actions>
                <analytics-controls v-model="controls" />
            </template>
        </page-header>

        <div class="vx-grid vx-gap-6 vx-grid-cols-1 lg:vx-grid-cols-12">
            <analytics-contacts-total-created
                class="lg:vx-col-span-4"
                :startDate="controls.start_date"
                :endDate="controls.end_date"
            >
            </analytics-contacts-total-created>

            <analytics-marketing-subscriptions-total-opt-ins
                class="lg:vx-col-span-4"
                :startDate="controls.start_date"
                :endDate="controls.end_date"
            >
            </analytics-marketing-subscriptions-total-opt-ins>

            <analytics-marketing-subscriptions-total-opt-outs
                class="lg:vx-col-span-4"
                :startDate="controls.start_date"
                :endDate="controls.end_date"
            >
            </analytics-marketing-subscriptions-total-opt-outs>

            <analytics-outbound-messages-total-messages-sent-by-audience-type
                class="lg:vx-col-span-12"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
            />

            <analytics-outbound-messages-total-contacts-messaged
                class="lg:vx-col-span-3"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
            >
            </analytics-outbound-messages-total-contacts-messaged>

            <analytics-outbound-messages-total-messages-sent-by-message-type
                class="lg:vx-col-span-3"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
            >
            </analytics-outbound-messages-total-messages-sent-by-message-type>

            <analytics-outbound-messages-total-messages-sent-by-message-type
                class="lg:vx-col-span-3"
                messageType="sms"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
            >
            </analytics-outbound-messages-total-messages-sent-by-message-type>

            <analytics-outbound-messages-total-messages-sent-by-message-type
                class="lg:vx-col-span-3"
                messageType="mms"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
            >
            </analytics-outbound-messages-total-messages-sent-by-message-type>

            <analytics-outbound-messages-delivery-rate
                class="lg:vx-col-span-6"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
            >
            </analytics-outbound-messages-delivery-rate>

            <analytics-outbound-messages-message-reply-rate
                class="lg:vx-col-span-6"
                :fetchParams="outboundMessagesMarketingCampaignsParams"
            >
                <template v-slot:title> Marketing Campaign Message Reply Rate </template>
            </analytics-outbound-messages-message-reply-rate>

            <analytics-outbound-messages-total-messages-sent-by-resource
                rateType="message-reply"
                :fetchParams="outboundMessagesMarketingCampaignsParams"
                class="lg:vx-col-span-6"
            >
                <template v-slot:title> Messages Sent per Marketing Campaign </template>
                <template v-slot:explainHeader> Messages Sent per Marketing Campaign </template>
                <template v-slot:explainContent>
                    <p>
                        <strong>Messages Sent</strong>: Gross Count of Messages sent by each Marketing Campaign during
                        the selected period.
                    </p>
                </template>
            </analytics-outbound-messages-total-messages-sent-by-resource>

            <analytics-outbound-messages-rate-type-by-resource
                rateType="click-through"
                :fetchParams="outboundMessagesMarketingCampaignsParams"
                class="lg:vx-col-span-6"
            >
                <template v-slot:title> Click-Through Rate per Marketing Campaign </template>
                <template v-slot:explainHeader> Click-Through Rate per Marketing Campaign </template>
                <template v-slot:explainContent>
                    <p>
                        <strong>Click-Through Rate</strong>: In the Context of Marketing Campaigns, in particular the
                        ones that provide a clickable link; this metric allows you to evaluate the success through the
                        relative clicks a campaigns receives relative to the quantity of messages delivered.
                    </p>
                </template>
            </analytics-outbound-messages-rate-type-by-resource>
        </div>
    </page-container>
</template>

<script setup>
import { ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageContainer from '~/components/general/PageContainer.vue';
import AnalyticsControls from './partial/AnalyticsControls.vue';
import AnalyticsMarketingSubscriptionsTotalOptIns from './charts/time-series/AnalyticsMarketingSubscriptionsTotalOptIns.vue';
import AnalyticsMarketingSubscriptionsTotalOptOuts from './charts/time-series/AnalyticsMarketingSubscriptionsTotalOptOuts.vue';
import AnalyticsContactsTotalCreated from './charts/time-series/AnalyticsContactsTotalCreated.vue';
import AnalyticsOutboundMessagesTotalContactsMessaged from './charts/time-series/AnalyticsOutboundMessagesTotalContactsMessaged.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByMessageType from './charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByMessageType.vue';
import AnalyticsOutboundMessagesDeliveryRate from './charts/time-series/AnalyticsOutboundMessagesDeliveryRate.vue';
import AnalyticsOutboundMessagesMessageReplyRate from './charts/time-series/AnalyticsOutboundMessagesMessageReplyRate.vue';
import AnalyticsOutboundMessagesRateTypeByResource from './charts/time-series/AnalyticsOutboundMessagesRateTypeByResource.vue';
import { controlDateRangeOptions } from '../../utils/analytics';
import AnalyticsOutboundMessagesTotalMessagesSentByResource from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByResource.vue';
import { computed } from 'vue';
import AnalyticsOutboundMessagesTotalMessagesSentByAudienceType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByAudienceType.vue';

const controls = ref(controlDateRangeOptions().find((option) => option.value === 'last_30_days'));

const outboundMessagesMarketingCampaignsParams = computed(() => ({
    range: [controls.value.start_date, controls.value.end_date],
    filters: {
        audience_types: ['marketing'],
        resource_types: ['campaign'],
    },
}));
</script>
